import React, { useState, useEffect, type HTMLAttributes } from 'react';
import styled from 'styled-components';
import {
    type ProgressiveDiscountViewModel,
    type ProgressiveDiscountDetailsViewModel,
    ProgressiveDiscountDetailsForProduct
} from 'autogen/swagger/all';
import { BorderRadiusMedium, ColorNordicGrey4 } from 'autogen/design-tokens/tokens';
import debounce from 'debounce';
import dynamic from 'helpers/dynamic';
import matchMedia from 'modules/helpers/matchMedia';
import ReactLoadingSpinner from 'modules/loadingSpinner';
import Spinner from 'modules/shared/circleSpinner';
import DefaultButton, { ButtonStyle } from 'modules/react-button/defaultButton';
import { SvgSprite } from 'modules/svgSprite';
import { useSpring, animated } from '@react-spring/web';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import toast from 'components/Toast';
import i18next from 'i18next';

const DiscountDetails = dynamic(() => import('./discountDetails'));

const Container = styled.div``;

const Button = styled(DefaultButton)<{ unfolded: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    height: 48px;

    && {
        ${({ unfolded }) => unfolded && `border-radius: ${BorderRadiusMedium} ${BorderRadiusMedium} 0 0;`};
    }
`;

const Text = styled.b`
    flex: 1;
    text-align: left;
`;

const Underline = styled.span`
    text-decoration: underline;
    margin-left: 10px;
`;

const ArrowIcon = styled(SvgSprite)`
    display: flex;
    flex: none;
    width: 10px;
    height: 10px;
    margin-left: 10px;
`;

const Arrow = animated<any>(ArrowIcon);

const DetailsAccordion = styled.div`
    background-color: ${ColorNordicGrey4};
    border-radius: 0 0 ${BorderRadiusMedium} ${BorderRadiusMedium};
`;

export interface IPDPHandle extends HTMLAttributes<HTMLDivElement> {
    model: ProgressiveDiscountViewModel;
    translations: {
        seeOffer: string;
    };
}

export default function PDPHandle({ model, translations, ...restProps }: IPDPHandle) {
    const { titleFirstPart, titleSecondPart, detailsLinkAction } = model;
    const { seeOffer } = translations;

    const [isMobile, setIsMobile] = useState<boolean>(matchMedia.isMobile);
    const [discountDetails, setDiscountDetails] = useState<ProgressiveDiscountDetailsViewModel>();
    const [showDiscountDetails, setShowDiscountDetails] = useState<boolean>(false);

    const handleResize = debounce((): void => {
        if (isMobile !== matchMedia.isMobile()) {
            // hide details if isMobile state changes
            setShowDiscountDetails(false);
        }
        setIsMobile(matchMedia.isMobile);
    }, 100);

    const arrowSpring = useSpring({
        transform: showDiscountDetails ? 'rotate(0deg)' : 'rotate(-90deg)',
        config: { tension: 250 }
    });

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return (): void => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    async function showDetailsAccordion(): Promise<void> {
        const { searchParams } = new URL(detailsLinkAction.url, window.location.origin);
        const productId = Number(searchParams.get('productid'));

        if (showDiscountDetails) {
            // close
            setShowDiscountDetails(false);
        } else {
            // open
            setShowDiscountDetails(true);
            await DiscountDetails;
            const details = await ProgressiveDiscountDetailsForProduct({ productId });
            setDiscountDetails(details);
        }
        UAEventTracking({
            eventCategory: 'progressiveDiscount',
            eventAction: 'accordion',
            eventLabel: showDiscountDetails ? 'open' : 'closed'
        });
    }

    async function openDetailsDrawer(): Promise<void> {
        const { searchParams } = new URL(detailsLinkAction.url, window.location.origin);
        const productId = Number(searchParams.get('productid'));
        let modal: any;

        function closeModal() {
            modal({ open: false });
        }

        try {
            const { default: createModal } = await import(/* webpackChunkName: "modal" */ 'modules/modal');
            const DrawerLayout = dynamic(() => import('modules/modal/layouts/drawerLayout'));
            modal = createModal({
                layout: {
                    component: <DrawerLayout fitContent />,
                    customTheme: { minWidth: '380px', maxWidth: '380px' }
                },
                onAfterClose: () => {
                    UAEventTracking({
                        eventCategory: 'progressiveDiscount',
                        eventAction: 'closeModal'
                    });
                }
            });

            modal({ open: true, content: <ReactLoadingSpinner /> });

            await DiscountDetails;
            const details = await ProgressiveDiscountDetailsForProduct({ productId });

            UAEventTracking({
                eventCategory: 'progressiveDiscount',
                eventAction: 'openModal'
            });

            modal({
                content: <DiscountDetails model={details} closeModal={closeModal} inModal />
            });
        } catch (err) {
            console.error(err);
            if (modal) {
                modal({ open: false });
            }
            toast.error(i18next.t(GeneralExceptionError));
        }
    }

    return (
        <Container {...restProps}>
            <Button
                unfolded={showDiscountDetails && !!discountDetails}
                buttonStyle={ButtonStyle.secondary}
                onClick={isMobile ? openDetailsDrawer : showDetailsAccordion}
            >
                <Text>{[titleFirstPart, titleSecondPart].filter(Boolean).join(' • ')}</Text>
                {isMobile ? (
                    <Underline>{seeOffer}</Underline>
                ) : (
                    <>
                        {showDiscountDetails && !discountDetails && <Spinner size={12} strokeWidth={1} timeout />}
                        <Arrow sprite="Arrow" style={arrowSpring} />
                    </>
                )}
            </Button>
            {showDiscountDetails && !!discountDetails && (
                <DetailsAccordion>
                    <DiscountDetails model={discountDetails} />
                </DetailsAccordion>
            )}
        </Container>
    );
}
